import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Snippet } from '@/v2/entities/KnowledgeSnippet';

interface SnippetsUrlState {
  selectedSnippetId: string | null;
  selectedPath: string | null;
  selectedVersionId: string | null;
}

interface UseSnippetsUrlStateParams {
  snippets: Snippet[] | undefined;
}

interface UseSnippetsUrlStateReturn {
  initialState: SnippetsUrlState;
  updateUrl: (state: SnippetsUrlState) => void;
}

/**
 * Custom hook to manage Snippets URL state
 * Handles reading from and writing to URL search parameters
 */
export function useSnippetsUrlState({
  snippets,
}: UseSnippetsUrlStateParams): UseSnippetsUrlStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Restore initial state from URL parameters
   */
  const restoreStateFromUrl = useCallback((): SnippetsUrlState => {
    const snippetId = searchParams.get('snippetId');
    const versionId = searchParams.get('versionId');

    if (!snippetId || !snippets) {
      return {
        selectedSnippetId: null,
        selectedPath: null,
        selectedVersionId: null,
      };
    }

    // Verify the snippet exists
    const snippet = snippets.find((s) => s.id === snippetId);

    if (snippet) {
      return {
        selectedSnippetId: snippet.id,
        selectedPath: snippet.path || null,
        selectedVersionId: versionId,
      };
    }

    // Invalid snippetId - return empty state
    return {
      selectedSnippetId: null,
      selectedPath: null,
      selectedVersionId: null,
    };
  }, [searchParams, snippets]);

  /**
   * Update URL with current state
   */
  const updateUrl = useCallback(
    (state: SnippetsUrlState) => {
      const newSearchParams = new URLSearchParams();

      // Only persist snippet selection in URL
      if (state.selectedSnippetId) {
        newSearchParams.set('snippetId', state.selectedSnippetId);

        if (state.selectedVersionId) {
          newSearchParams.set('versionId', state.selectedVersionId);
        }
      }

      if (newSearchParams.toString() === searchParams.toString()) {
        return;
      }

      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  // Get initial state from URL
  const initialState = restoreStateFromUrl();

  return {
    initialState,
    updateUrl,
  };
}
